import {
  LayoutDashboard, ListOrdered, BarChart2, FileText,
  FlaskConical, BrainCircuit, Settings, MoreHorizontal, ScrollText, PlayCircle, Target,
} from "lucide-react";
import type { Page } from "@/types/trading";

const _MORE_ID = "__more__";  // sentinel — not a real Page, triggers sheet toggle

// Primary 4 + More — paper placed next to Trades (Portfolio)
const NAV_ITEMS: { id: string; label: string; icon: React.ReactNode }[] = [
  { id: "dashboard", label: "Home",    icon: <LayoutDashboard size={18} /> },
  { id: "trade",     label: "Trades",  icon: <ListOrdered size={18} /> },
  { id: "paper",     label: "Paper",   icon: <FileText size={18} /> },
  { id: "pnl",       label: "P&L",     icon: <BarChart2 size={18} /> },
  { id: _MORE_ID,    label: "More",    icon: <MoreHorizontal size={18} /> },
];

// Secondary pages accessible from "More" sheet — logs kept last
const MORE_PAGES: { id: Page; label: string; icon: React.ReactNode }[] = [
  { id: "replay",   label: "Replay",        icon: <PlayCircle size={18} /> },
  { id: "lab",      label: "Lab",           icon: <FlaskConical size={18} /> },
  { id: "strategy-pnl", label: "Strategy P&L", icon: <Target size={18} /> },
  { id: "chat",     label: "Chat",          icon: <BrainCircuit size={18} /> },
  { id: "settings", label: "Settings",      icon: <Settings size={18} /> },
  { id: "logs",     label: "Logs",          icon: <ScrollText size={18} /> },
];

interface MobileNavProps {
  page: Page;
  setPage: (p: Page) => void;
  moreOpen: boolean;
  setMoreOpen: (v: boolean) => void;
}

export default function MobileNav({ page, setPage, moreOpen, setMoreOpen }: MobileNavProps) {
  const isMorePage = MORE_PAGES.some(p => p.id === page);

  return (
    <>
      {/* More sheet overlay */}
      {moreOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden" onClick={() => setMoreOpen(false)} />
          <div className="fixed bottom-14 left-0 right-0 z-50 md:hidden px-3 pb-1">
            <div className="bg-[#111118] border border-zinc-800/60 rounded-2xl p-3 shadow-2xl">
              <p className="text-zinc-600 text-[11px] uppercase tracking-wider font-semibold px-2 mb-2">More Pages</p>
              <div className="grid grid-cols-3 gap-1">
                {MORE_PAGES.map(item => (
                  <button
                    key={item.id}
                    onClick={() => { setPage(item.id); setMoreOpen(false); }}
                    className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl transition ${
                      page === item.id
                        ? "bg-brand/15 text-brand"
                        : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/60"
                    }`}
                  >
                    {item.icon}
                    <span className="text-[10px] font-semibold">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Bottom nav bar — visible only on mobile */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 h-14 bg-[#08080d] border-t border-zinc-800/50 flex items-center justify-around px-1 shrink-0">
        {NAV_ITEMS.map(item => {
          const isMore = item.id === _MORE_ID;
          const active = isMore ? (moreOpen || isMorePage) : page === item.id;
          return (
            <button
              key={item.id}
              onClick={() => {
                if (isMore) {
                  setMoreOpen(!moreOpen);
                } else {
                  setMoreOpen(false);
                  if (item.id !== _MORE_ID) setPage(item.id as Page);
                }
              }}
              className={`flex flex-col items-center gap-0.5 py-1 px-3 rounded-lg transition ${
                active ? "text-brand" : "text-zinc-600"
              }`}
            >
              {item.icon}
              <span className="text-[11px] font-semibold">{item.label}</span>
            </button>
          );
        })}
      </nav>
    </>
  );
}
